// Individual session card with essential info and action buttons
import { useMemo, useState } from 'react'
import { Play, Square, Users, BarChart3, DoorOpen } from 'lucide-react'

import { Progress } from '@/ui/progress'
import { Card, CardContent } from '@/ui/card'
import { Button } from '@/ui/button'
import { ConfirmDialog } from '@/core/components/ConfirmDialog'
import { useConfirm } from '@/core/hooks/useConfirm'
import { useToast } from '@/core/hooks/useToast'
import { useClassSessionStore } from '@/features/classes/stores'
import type { ClassSession, SessionAttendee, Student, OpenCheckinPayload } from '@/features/classes/stores'
import { cn } from '@/core/lib/utils'

import { fmtDate, fmtTimeRange, languageBadge } from '../utils/formatters'

import { OpenCheckinModal } from './OpenCheckinModal'
import { SessionStatusBadge } from './StatusBadges'

interface SessionCardProps {
  session: ClassSession
  onDetails: () => void
}

export function SessionCard({
  session,
  onDetails,
}: SessionCardProps) {
  const courses = useClassSessionStore((s) => s.courses)
  const openCheckin = useClassSessionStore((s) => s.openCheckin)
  const beginClass = useClassSessionStore((s) => s.beginClass)
  const closeSession = useClassSessionStore((s) => s.closeSession)

  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm()
  const { show: showToast, ToastUI } = useToast()
  const [checkinOpen, setCheckinOpen] = useState(false)

  function handleOpenCheckin() {
    setCheckinOpen(true)
  }

  async function executeOpenCheckin(payload: OpenCheckinPayload) {
    try {
      await openCheckin(session.id, payload)
    } finally {
      setCheckinOpen(false)
    }
  }

  const handleEndClass = async () => {
    const confirmed = await confirm({
      title: 'End class?',
      description: 'Certificates will be generated for all students who completed the diagnostic.',
      confirmText: 'End Class',
      cancelText: 'Cancel',
      variant: 'destructive',
    })
    if (!confirmed) {
      return
    }
    try {
      const result = await closeSession(session.id)
      showToast(
        `Class ended. ${result.certificatesCreated} certificate(s) created and ${result.certificatesEmailed} email(s) queued.`,
        'success'
      )
    } catch (err) {
      console.error(err)
      showToast('Failed to end class. Please try again.', 'error')
    }
  }

  // Subscribe directly to attendees + students so the card re-renders
  // whenever loadSessionAttendees() updates the store (initial load or
  // real-time refresh).  Subscribing to getSessionAttendees (a stable fn ref)
  // would NOT trigger re-renders when the underlying data changes.
  // Raw array references only change when the store is updated with new data,
  // so this avoids unnecessary re-renders on unrelated store changes.
  const rawAttendees = useClassSessionStore((s) => s.attendees)
  const rawStudents = useClassSessionStore((s) => s.students)
  const attendees = useMemo((): Array<SessionAttendee & { student: Student }> =>
    rawAttendees
      .filter((a) => a.sessionId === session.id)
      .map((a) => ({ ...a, student: rawStudents.find((st) => st.id === a.studentId)! }))
      .filter((a) => a.student),
  [rawAttendees, rawStudents, session.id])

  // The list view does not load attendees for every session, but the backend
  // already returns the correct spot counts. Use the session data as the source
  // of truth for registered count, and fall back to the live attendee store
  // when it has been hydrated (e.g. after opening the detail page or via WS).
  const attendeesLoadedForSession = rawAttendees.some((a) => a.sessionId === session.id)
  const spotsAvailable = session.spotsAvailable ?? session.maxAttendees ?? 0
  const registeredCount = attendeesLoadedForSession
    ? attendees.length
    : Math.max(0, (session.maxAttendees ?? 0) - spotsAvailable)

  const course = courses.find((c) => c.slug === session.courseId)
  const langBadge = languageBadge(session.language)

  // Per-course accent color so sessions for different courses are easy to tell
  // apart at a glance. Prefer the course's certificate color; otherwise derive
  // a stable color from the course id.
  const FALLBACK_COLORS = ['#1d4ed8', '#b45309', '#047857', '#a21caf', '#be123c', '#0e7490']
  const accentColor = course?.certificateColor ?? (() => {
    const key = String(session.courseId)
    let hash = 0
    for (let i = 0; i < key.length; i++) {hash = (hash * 31 + key.charCodeAt(i)) >>> 0}
    return FALLBACK_COLORS[hash % FALLBACK_COLORS.length]
  })()
  const withScore = attendees.filter((a) => a.quizScore !== null)
  const avg = withScore.length > 0
    ? Math.round(withScore.reduce((s, a) => s + (a.quizScore ?? 0), 0) / withScore.length)
    : null

  return (
    <Card
      data-testid={`session-card-${session.id}`}
      className={cn('border-l-4', session.status === 'active' || session.status === 'checking' ? 'border-green-300 shadow-sm' : '')}
      style={{ borderLeftColor: accentColor }}
    >
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: accentColor }}
                aria-hidden="true"
              />
              <h3 className="font-semibold text-navy">{course?.name ?? session.courseId}</h3>
              <SessionStatusBadge status={session.status} />
              <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', langBadge.className)}>
                {langBadge.label}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              📍 {session.locationName ?? session.location ?? '—'} · 📅 {fmtDate(session.date)} ·{' '}
              🕐 {fmtTimeRange(session.startTime, session.endTime)} · 👤 {session.instructorName}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="flex items-center gap-1.5 text-slate-500">
                {/* Live-pulse dot — only for active or checking sessions */}
                {(session.status === 'active' || session.status === 'checking') && (
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                )}
                {session.status !== 'active' && session.status !== 'checking' && <Users className="w-3.5 h-3.5" />}
                <span className="inline-flex items-center gap-1">
                  {registeredCount} student{registeredCount !== 1 ? 's' : ''} registered
                </span>
              </span>
              {session.maxAttendees && session.maxAttendees > 0 && (
                <span className="flex flex-col gap-0.5 min-w-[100px]">
                  <span className={cn(
                    'flex items-center gap-1 text-xs',
                    registeredCount >= session.maxAttendees ? 'text-red-500 font-medium' :
                    registeredCount >= session.maxAttendees * 0.8 ? 'text-orange-500' :
                    'text-green-600'
                  )}>
                    {registeredCount >= session.maxAttendees ? '⛔' :
                     registeredCount >= session.maxAttendees * 0.8 ? '⚠️' : '✓'}
                    {registeredCount} / {session.maxAttendees} spots
                  </span>
                  <Progress
                    value={Math.min((registeredCount / session.maxAttendees) * 100, 100)}
                    className="h-1.5 w-full"
                  />
                </span>
              )}
              {avg !== null && (
                <span className="flex items-center gap-1 text-slate-500">
                  <BarChart3 className="w-3.5 h-3.5" />
                  Avg. diagnostic: {avg}%
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {session.status === 'draft' && (
              <Button size="sm" onClick={handleOpenCheckin}>
                <DoorOpen className="w-3.5 h-3.5" />
                Open Check-in
              </Button>
            )}
            {session.status === 'checking' && (
              <Button size="sm" variant="secondary" onClick={() => beginClass(session.id)}>
                <Play className="w-3.5 h-3.5" />
                Begin Class
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onDetails}>
              <Users className="w-3.5 h-3.5" />
              Details
            </Button>
            {session.status === 'active' && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleEndClass}
              >
                <Square className="w-3.5 h-3.5" />
                End Class
              </Button>
            )}
          </div>
        </div>

      </CardContent>
      {checkinOpen && (
        <OpenCheckinModal
          session={session}
          onClose={() => setCheckinOpen(false)}
          onConfirm={executeOpenCheckin}
        />
      )}
      <ConfirmDialog
        open={isOpen}
        title={options.title}
        description={options.description}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      {ToastUI}
    </Card>
  )
}
